import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wallet, Filter, Sparkles, Upload, FileText, Image as ImageIcon, Loader2, Eye } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from "recharts";

const categoryConfig = {
  salary:   { label: "Salary",   color: "hsl(153, 34%, 27%)" },
  tax:      { label: "Tax",      color: "hsl(11, 67%, 62%)" },
  software: { label: "Software", color: "hsl(30, 43%, 64%)" },
  rent:     { label: "Rent",     color: "hsl(79, 30%, 75%)" },
  other:    { label: "Other",    color: "hsl(42, 87%, 89%)" },
};

export default function ExpensesPage() {
  const { token } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [extracting, setExtracting] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null); // {base64, mime, filename}
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewExpense, setPreviewExpense] = useState(null);
  const fileInputRef = useRef(null);

  const emptyForm = {
    category: "other",
    description: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    recurring: false,
    vendor: "",
    tax_amount: "",
    account_code: "",
    notes: "",
  };
  const [formData, setFormData] = useState(emptyForm);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchExpenses = useCallback(async () => {
    try {
      const params = categoryFilter !== "all" ? { category: categoryFilter } : {};
      const response = await axios.get(`${API}/expenses`, { headers, params });
      setExpenses(response.data);
    } catch (err) {
      console.error("Failed to fetch expenses:", err);
      toast.error("Failed to fetch expenses");
    } finally { setLoading(false); }
  }, [headers, categoryFilter]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chart-of-accounts`, {
        headers, params: { type: "Expense", active_only: true },
      });
      setAccounts(res.data);
    } catch (err) { console.warn("Failed to load expense accounts:", err); }
  }, [headers]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const resetForm = () => {
    setEditingExpense(null);
    setReceiptPreview(null);
    setFormData({ ...emptyForm, date: new Date().toISOString().split("T")[0] });
  };

  const handleExtractReceipt = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setExtracting(true);
    try {
      const res = await axios.post(`${API}/expenses/extract-receipt`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      setFormData((prev) => ({
        ...prev,
        vendor: d.vendor || prev.vendor,
        description: d.description || prev.description || (d.vendor ? `Purchase from ${d.vendor}` : prev.description),
        amount: d.amount ? String(d.amount) : prev.amount,
        tax_amount: d.tax_amount != null ? String(d.tax_amount) : prev.tax_amount,
        date: d.date || prev.date,
        category: d.suggested_category || prev.category,
      }));
      setReceiptPreview({
        base64: d.receipt_base64,
        mime: d.receipt_mime,
        filename: d.receipt_filename,
      });
      toast.success("Receipt scanned — please review the details");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extract receipt");
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount) || 0,
        date: formData.date,
        recurring: formData.recurring,
        vendor: formData.vendor || null,
        tax_amount: formData.tax_amount ? parseFloat(formData.tax_amount) : null,
        account_code: formData.account_code || null,
        notes: formData.notes || null,
        receipt_base64: receiptPreview?.base64 || editingExpense?.receipt_base64 || null,
        receipt_mime: receiptPreview?.mime || editingExpense?.receipt_mime || null,
        receipt_filename: receiptPreview?.filename || editingExpense?.receipt_filename || null,
      };
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload, { headers });
        toast.success("Expense updated");
      } else {
        await axios.post(`${API}/expenses`, payload, { headers });
        toast.success("Expense created");
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save expense");
    }
  };

  const handleEdit = (exp) => {
    setEditingExpense(exp);
    setFormData({
      category: exp.category || "other",
      description: exp.description || "",
      amount: exp.amount?.toString() || "",
      date: exp.date,
      recurring: !!exp.recurring,
      vendor: exp.vendor || "",
      tax_amount: exp.tax_amount != null ? String(exp.tax_amount) : "",
      account_code: exp.account_code || "",
      notes: exp.notes || "",
    });
    setReceiptPreview(exp.receipt_base64 ? {
      base64: exp.receipt_base64,
      mime: exp.receipt_mime,
      filename: exp.receipt_filename,
    } : null);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;
    try {
      await axios.delete(`${API}/expenses/${id}`, { headers });
      toast.success("Expense deleted");
      fetchExpenses();
    } catch (err) { console.error("Failed to delete expense:", err); toast.error("Failed to delete expense"); }
  };

  const openReceiptPreview = (exp) => {
    setPreviewExpense(exp);
    setPreviewOpen(true);
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(v || 0);

  const categoryTotals = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});
  const chartData = Object.entries(categoryTotals).map(([c, v]) => ({
    name: categoryConfig[c]?.label || c,
    value: v,
    fill: categoryConfig[c]?.color || "#ccc",
  }));
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  const receiptDataUrl = (b64, mime) => (b64 ? `data:${mime || "image/jpeg"};base64,${b64}` : null);

  return (
    <DashboardLayout title="Expenses">
      <div className="space-y-6" data-testid="expenses-page">
        {/* Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle className="section-title">Total Expenses</CardTitle></CardHeader>
            <CardContent>
              <p className="stat-value text-3xl negative-value">{formatCurrency(totalExpenses)}</p>
              <p className="text-sm text-muted-foreground mt-2">All time expenses</p>
            </CardContent>
          </Card>
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="section-title">Expenses by Category</CardTitle></CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={chartData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                        {chartData.map((entry, i) => <Cell key={entry.name || `cell-${i}`} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-muted-foreground">No expense data yet</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Filter + Add */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="salary">Salary</SelectItem>
                <SelectItem value="tax">Tax</SelectItem>
                <SelectItem value="software">Software</SelectItem>
                <SelectItem value="rent">Rent</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-expense-btn">
                <Plus className="w-4 h-4 mr-2" />Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingExpense ? "Edit Expense" : "Add New Expense"}</DialogTitle>
                <DialogDescription>
                  Upload a receipt to auto-fill fields, or enter details manually.
                </DialogDescription>
              </DialogHeader>

              {/* AI Receipt Scan */}
              <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Scan receipt with AI</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,application/pdf,.pdf,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={(e) => handleExtractReceipt(e.target.files?.[0])}
                    data-testid="receipt-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={extracting}
                    data-testid="scan-receipt-btn"
                  >
                    {extracting
                      ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning...</>)
                      : (<><Upload className="w-4 h-4 mr-2" />Upload Receipt</>)}
                  </Button>
                  {receiptPreview && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {receiptPreview.mime?.includes("pdf")
                        ? <FileText className="w-4 h-4" />
                        : <ImageIcon className="w-4 h-4" />}
                      <span className="truncate max-w-[200px]">{receiptPreview.filename}</span>
                      <Button
                        type="button" size="sm" variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => setReceiptPreview(null)}
                      >Remove</Button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Images (PNG/JPG/WEBP) or PDF. Auto-extracts vendor, date, total, GST. Max 10MB.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salary">Salary</SelectItem>
                        <SelectItem value="tax">Tax</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="rent">Rent</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="expense-date-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <Input
                      value={formData.vendor}
                      onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                      placeholder="e.g., Officeworks"
                      data-testid="expense-vendor-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account (COA)</Label>
                    <Select
                      value={formData.account_code || "__none__"}
                      onValueChange={(v) => setFormData({ ...formData, account_code: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="expense-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="__none__">— None —</SelectItem>
                        {accounts.map((a) => (
                          <SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Office supplies"
                    required
                    data-testid="expense-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount (AUD) *</Label>
                    <Input
                      type="number" step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00" required
                      data-testid="expense-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>GST / Tax (AUD)</Label>
                    <Input
                      type="number" step="0.01"
                      value={formData.tax_amount}
                      onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="expense-tax-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="recurring"
                    checked={formData.recurring}
                    onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                    className="rounded border-input"
                    data-testid="expense-recurring-checkbox"
                  />
                  <Label htmlFor="recurring" className="cursor-pointer">This is a recurring expense</Label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-expense-btn">
                    {editingExpense ? "Update" : "Add"} Expense
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Expenses Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading expenses...</div>
              </div>
            ) : expenses.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Wallet className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No expenses yet</h3>
                <p className="text-muted-foreground mt-1">Add your first expense to start tracking</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header">Date</TableHead>
                    <TableHead className="table-header">Category</TableHead>
                    <TableHead className="table-header">Vendor</TableHead>
                    <TableHead className="table-header">Description</TableHead>
                    <TableHead className="table-header">Account</TableHead>
                    <TableHead className="table-header">Receipt</TableHead>
                    <TableHead className="table-header text-right">Amount</TableHead>
                    <TableHead className="table-header text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((exp) => (
                    <TableRow key={exp.id} className="hover:bg-accent/50 transition-colors">
                      <TableCell className="data-cell">{exp.date}</TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          style={{
                            backgroundColor: categoryConfig[exp.category]?.color + "20",
                            color: categoryConfig[exp.category]?.color,
                          }}
                        >
                          {categoryConfig[exp.category]?.label || exp.category}
                        </Badge>
                      </TableCell>
                      <TableCell>{exp.vendor || "-"}</TableCell>
                      <TableCell>{exp.description}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {exp.account_code || "-"}
                      </TableCell>
                      <TableCell>
                        {exp.receipt_base64 ? (
                          <Button variant="ghost" size="sm" onClick={() => openReceiptPreview(exp)} data-testid={`view-receipt-${exp.id}`}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        ) : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-right data-cell negative-value font-medium">
                        {formatCurrency(exp.amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(exp)} data-testid={`edit-expense-${exp.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(exp.id)}
                            data-testid={`delete-expense-${exp.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Receipt preview dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Receipt Preview</DialogTitle>
              <DialogDescription>{previewExpense?.receipt_filename}</DialogDescription>
            </DialogHeader>
            {previewExpense?.receipt_base64 && (
              previewExpense.receipt_mime?.includes("pdf") ? (
                <iframe
                  title="receipt-pdf"
                  src={receiptDataUrl(previewExpense.receipt_base64, previewExpense.receipt_mime)}
                  className="w-full h-[600px] border rounded"
                />
              ) : (
                <img
                  src={receiptDataUrl(previewExpense.receipt_base64, previewExpense.receipt_mime)}
                  alt="Receipt"
                  className="w-full max-h-[70vh] object-contain rounded border"
                />
              )
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
